import { ampli } from "@/config";
import type { AcknowledgementIntent } from "@/models/acknowledgement/acknowledgementWrites";

/**
 * What wrote an acknowledgement: the shortcut key, the row's button, the header's button over the ticked rows, the
 * reply that handled the row, or Ctrl/Cmd+Z taking an action back.
 */
export type AcknowledgementTrigger = "keyboard" | "rowAction" | "bulkAction" | "reply" | "undo";

/** What opened the Context panel: the shortcut key, the row's Context button, or the popover's Context tab. */
export type ContextOpenTrigger = "keyboard" | "rowAction" | "tab";

/** How a composer's send was pressed: Enter, or the Send button. */
export type ReplyTrigger = "keyboard" | "button";

/** Where a reply was written: the row's inline reply, or the popover's composer under the History or Context tab. */
export type ReplySource = "row" | "history" | "context";

/**
 * Reports one acknowledgement to Amplitude: a channel event for a watermark, a message event for a message or a
 * required TMS event. One call per intent, so a bulk action over ticked rows reports each target it handled.
 */
export function trackAcknowledgement(
  workspaceId: string,
  intent: AcknowledgementIntent,
  trigger: AcknowledgementTrigger,
): void {
  const acknowledged = intent.action === "acknowledged";
  // The tracking plan has no task event yet; a task's acknowledgement is reported by the item it carries, if any.
  if (intent.subject === "task") return;
  if (intent.subject === "feed") {
    const properties = { workspaceId, feedId: intent.feedId, trigger };
    if (acknowledged) ampli.channelAcknowledged(properties);
    else ampli.channelUnacknowledged(properties);
    return;
  }
  const properties =
    intent.subject === "feed_item"
      ? { workspaceId, feedItemId: intent.feedItemId, subject: "feed_item" as const, trigger }
      : { workspaceId, feedItemId: intent.anchorId, subject: "tms_event" as const, trigger };
  if (acknowledged) ampli.messageAcknowledged(properties);
  else ampli.messageUnacknowledged(properties);
}
